<template>
  <div class="settings">
    <DietInfoSection
      v-model:name="dietName"
      v-model:description="dietDescription"
      :diet-type="dietType"
      :editable="canDietBeEdited"
      :can-change="canChangeDaysCount"
      :days-count="dietDaysCount"
      :has-dates="hasAssignedDates"
      @change="openDaysCountModal"
    />
    <Divider />
    <DietFiltersSection
      :enabled="hasPerm('accounts.diet_settings_search_in')"
      :filters="filterItems"
      @update="updateFilter"
      @upgrade="showAppUpgradePlanModal"
    />
    <Divider />
    <DietNutrientsSection :chosen="chosenNutrients" @edit="openNutrientsModal" />
    <Divider />
    <DietAdvancedSettingSection
      :active="inevitableLossesActive"
      :repeated-current-diet-active="repeatedCurrentDietActive"
      :repeated-diets-count="repeatedDietsCount"
      :repeated-previous-diets-active="repeatedPreviousDietsActive"
      :show-repeated-dishes-settings="dietType === 'patient'"
      :value="inevitableLossesInput"
      @active="handleUpdateInevitableLosesActive"
      @repeated-current-diet-active="updateRepeatedCurrentDietActive"
      @repeated-diets-count="updateRepeatedDietsCount"
      @repeated-previous-diets-active="updateRepeatedPreviousDietsActive"
      @value="updateInevitableLosses"
    />
    <DietDaysCountModal
      :day-items="daysCountItems"
      :open="isDaysCountModalOpen"
      :preset="selectedDaysCountPreset"
      :preset-items="daysCountPresetItems"
      :selected-days="selectedDaysCount"
      :target-days-count="targetDaysCount"
      :warning="removedDaysMessage"
      @close="isDaysCountModalOpen = false"
      @days="selectedDaysCount = $event"
      @preset="updateDaysCountPreset"
      @save="saveDaysCount"
    />
    <DietNutrientsModal
      :chosen="chosenNutrients"
      :open="isNutrientsModalOpen"
      @close="isNutrientsModalOpen = false"
      @save="saveNutrients"
    />
  </div>
</template>

<script lang="ts" setup>
  import { S } from '@mobily/ts-belt'
  import { useDebounceFn } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, ref, watch } from 'vue'

  import { Divider } from '@/components/ui'
  import { DIET_MAX_DAYS_COUNT, DIET_MIN_DAYS_COUNT } from '@/const'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { getDietDaysCount } from '@/utils/dietDays'

  import type { Nutrients } from '@/utils/nutrients'

  import DietAdvancedSettingSection from './components/DietAdvancedSettingSection/DietAdvancedSettingSection.vue'
  import DietDaysCountModal from './components/DietDaysCountModal/DietDaysCountModal.vue'
  import DietFiltersSection from './components/DietFiltersSection/DietFiltersSection.vue'
  import DietInfoSection from './components/DietInfoSection/DietInfoSection.vue'
  import DietNutrientsModal from './components/DietNutrientsSection/DietNutrientsModal.vue'
  import DietNutrientsSection from './components/DietNutrientsSection/DietNutrientsSection.vue'

  const globalStore = useGlobalStore()
  const dietsStore = useDietsStore()
  const { hasPerm, showAppUpgradePlanModal } = globalStore
  const {
    updateDietName,
    updateDietDescription,
    updateDaysCount,
    updateDishSearchFilters,
    updatechosenNutrients,
    updateInevitableLossesActive,
    updateInevitableLossesValue,
    updateRepeatedCurrentDietActive,
    updateRepeatedDietsCount,
    updateRepeatedPreviousDietsActive,
  } = dietsStore

  const { diet, dietType, canDietBeEdited } = storeToRefs(dietsStore)

  const daysCountItems = Array.from(
    { length: DIET_MAX_DAYS_COUNT - DIET_MIN_DAYS_COUNT + 1 },
    (_, index) => {
      const value = DIET_MIN_DAYS_COUNT + index
      return { id: value, label: `${value} dni` }
    },
  )
  type DaysCountPreset = 7 | 14 | 21 | 28 | 'custom'
  const daysCountPresetItems: { id: DaysCountPreset; label: string }[] = [
    { id: 7, label: '7 dni' },
    { id: 14, label: '14 dni' },
    { id: 21, label: '21 dni' },
    { id: 28, label: '28 dni' },
    { id: 'custom', label: 'Niestandardowa' },
  ]
  const isDaysCountModalOpen = ref(false)
  const isNutrientsModalOpen = ref(false)
  const selectedDaysCount = ref<number | null>(null)
  const selectedDaysCountPreset = ref<DaysCountPreset>('custom')

  const dietName = computed({
    get() {
      return diet.value?.data.name ?? ''
    },
    set(name: string) {
      if (name.length > 0) {
        updateDietName(S.trim(name))
      } else {
        updateDietName(dietName.value[0] ?? '')
      }
    },
  })

  const dietDescription = computed({
    get() {
      return diet.value?.data.description ?? ''
    },
    set(description) {
      updateDietDescription(description)
    },
  })
  const dietDaysCount = computed(() => getDietDaysCount(diet.value))
  const hasAssignedDates = computed(() => Boolean(diet.value?.data.start_date))
  const canChangeDaysCount = computed(() => canDietBeEdited.value && !hasAssignedDates.value)
  const targetDaysCount = computed<number | null>(() =>
    selectedDaysCountPreset.value === 'custom'
      ? selectedDaysCount.value
      : selectedDaysCountPreset.value,
  )
  const removedDaysMessage = computed(() => {
    const target = targetDaysCount.value
    if (target === null || target >= dietDaysCount.value) return null

    const firstRemovedDay = target + 1
    const removedDays =
      firstRemovedDay === dietDaysCount.value
        ? `dzień ${firstRemovedDay} zostanie usunięty!`
        : `dni ${firstRemovedDay}–${dietDaysCount.value} zostaną usunięte!`

    return `Skracasz jadłospis, ${removedDays}`
  })

  const inevitableLossesActive = computed(() => {
    console.log('inevitableLossesActive', diet.value?.data.inevitable_losses.active)
    return Boolean(diet.value?.data.inevitable_losses.active)
  })
  const inevitableLossesValue = computed(() => {
    return diet.value?.data.inevitable_losses.value ?? 0
  })
  const inevitableLossesInput = ref('')
  const repeatedCurrentDietActive = computed(
    () => diet.value?.data.repeated_current_diet_active ?? false,
  )
  const repeatedPreviousDietsActive = computed(
    () => diet.value?.data.repeated_previous_diets_active ?? true,
  )
  const repeatedDietsCount = computed(() => diet.value?.data.repeated_diets_count ?? 5)
  const updateInevitableLossesValueDebounced = useDebounceFn((value: number) => {
    updateInevitableLossesValue(value)
  }, 300)

  watch(
    inevitableLossesValue,
    value => {
      inevitableLossesInput.value = String(value)
    },
    { immediate: true },
  )

  const handleUpdateInevitableLosesActive = () => {
    if (hasPerm('accounts.diet_inevitable_losses')) {
      const active = !diet.value?.data.inevitable_losses.active
      updateInevitableLossesActive(active)
    } else {
      showAppUpgradePlanModal()
    }
  }
  const updateInevitableLosses = (value: string) => {
    inevitableLossesInput.value = value
    const parsedValue = Number(value)
    if (Number.isNaN(parsedValue)) return

    const normalizedValue = Math.min(99, Math.max(0.1, Math.round(parsedValue * 10) / 10))
    void updateInevitableLossesValueDebounced(normalizedValue)
  }

  const openDaysCountModal = () => {
    if (!canChangeDaysCount.value) return

    selectedDaysCount.value = dietDaysCount.value
    selectedDaysCountPreset.value =
      daysCountPresetItems.find(item => item.id === dietDaysCount.value)?.id ?? 'custom'
    isDaysCountModalOpen.value = true
  }

  const updateDaysCountPreset = (preset: number | string) => {
    selectedDaysCountPreset.value = preset as DaysCountPreset
    if (typeof preset === 'number') selectedDaysCount.value = preset
  }

  const saveDaysCount = () => {
    const target = targetDaysCount.value
    if (!canChangeDaysCount.value || target === null) return

    if (target !== dietDaysCount.value) {
      updateDaysCount(target)
    }

    isDaysCountModalOpen.value = false
  }
  const filterDiaryFree = computed({
    get() {
      return diet.value?.data.dish_search_filters.diary_free ?? false
    },
    set(value) {
      updateDishSearchFilters({
        key: 'diary_free',
        value,
      })
    },
  })
  const filterGlutenFree = computed({
    get() {
      return diet.value?.data.dish_search_filters.gluten_free ?? false
    },
    set(value) {
      updateDishSearchFilters({
        key: 'gluten_free',
        value,
      })
    },
  })
  const filterVegan = computed({
    get() {
      return diet.value?.data.dish_search_filters.vegan ?? false
    },
    set(value) {
      updateDishSearchFilters({
        key: 'vegan',
        value,
      })
    },
  })
  const filterVegetarian = computed({
    get() {
      return diet.value?.data.dish_search_filters.vegetarian ?? false
    },
    set(value) {
      updateDishSearchFilters({
        key: 'vegetarian',
        value,
      })
    },
  })
  const filterItems = computed(() => [
    { key: 'vegan', label: 'Wegańska', value: filterVegan.value },
    { key: 'vegetarian', label: 'Wegetariańska', value: filterVegetarian.value },
    { key: 'gluten_free', label: 'Bezglutenowa', value: filterGlutenFree.value },
    { key: 'diary_free', label: 'Bezmleczna', value: filterDiaryFree.value },
  ])
  const updateFilter = (key: string, value: boolean) => {
    updateDishSearchFilters({
      key: key as 'diary_free' | 'gluten_free' | 'vegan' | 'vegetarian',
      value,
    })
  }
  const chosenNutrients = computed<Nutrients[]>(() => {
    return (
      diet.value?.data.choosen_nutrients ?? [
        'protein',
        'fat',
        'carbohydrates',
        'fiber',
        'carbohydrate_exchanger',
      ]
    )
  })
  const openNutrientsModal = () => {
    if (hasPerm('accounts.diet_microelements')) {
      isNutrientsModalOpen.value = true
    } else {
      showAppUpgradePlanModal()
    }
  }
  const saveNutrients = (nutrients: Nutrients[]) => {
    const unchanged = nutrients.every(
      (nutrient, index) => nutrient === chosenNutrients.value[index],
    )
    if (nutrients.length !== chosenNutrients.value.length || !unchanged) {
      updatechosenNutrients(nutrients)
    }

    isNutrientsModalOpen.value = false
  }
</script>

<style lang="scss">
.settings {
  width: calc(var(--diet-detail-viewport-width, 100%) + 64px) !important;
  padding: 32px;
  margin-left: -32px;
  background: var(--color-background-bg-secondary);
  display: flex;
  flex-direction: column;
  gap: 16px;


  .settings-box {
    background: var(--color-background-bg-primary, #fff);
    border: 1px solid var(--color-border-border-secondary, #eaecf0);
    border-radius: var(--radius-xl, 12px);
  }

  .name {
    width: 100%;
    max-width: 800px;
    font-size: 16px;
    line-height: 19px;
  }

  .days-count-field {
    max-width: 800px;
  }

  .days-count-field__input {
    width: 176px;
  }

  .description {
    max-width: 800px;
    max-height: 200px;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .description::-webkit-scrollbar {
    width: 0;
  }

  .inevitable-losses__input {
    width: 120px;
  }

  .inevitable-losses__info {
    color: var(--color-secondary-text);
  }

}
</style>
